import { sb } from "../../../lib/supabase";

export interface PveRunRecord {
  profileId: string;
  reachedStage: number;
  cleared: boolean;
  xpEarned: number;
  goldEarned: number;
  chestEarned: number; // 0 or 1
  variant?: string;    // A/B bucket that played this run ('control' | 'spells')
}

/** How many runs today (UTC midnight reset). */
export async function getTodayRunCount(profileId: string): Promise<number> {
  const todayStart = new Date();
  todayStart.setUTCHours(0, 0, 0, 0);
  const rows = await sb.get(
    `pve_runs?profile_id=eq.${encodeURIComponent(profileId)}&created_at=gte.${todayStart.toISOString()}&select=id`
  );
  return rows.length;
}

/**
 * Insert a pve_runs record.
 * Returns true on success. Caller MUST abort rewards on false.
 */
export async function insertPveRun(record: PveRunRecord): Promise<boolean> {
  return sb.insert("pve_runs", {
    profile_id: record.profileId,
    reached_stage: record.reachedStage,
    cleared: record.cleared,
    xp_earned: record.xpEarned,
    gold_earned: record.goldEarned,
    chest_earned: record.chestEarned,
    variant: record.variant ?? null,
  });
}

export interface PvePlayerPatch {
  pve_condition: number;
  pve_condition_updated_at: string;
  pve_best_stage?: number;
  pve_trial_chests?: number;
  bonus_points?: number;
  match_gold?: number;
}

/** Patch players row with all PVE fields + reward deltas (absolute values, not deltas). */
export async function patchPvePlayer(playerName: string, patch: PvePlayerPatch): Promise<boolean> {
  const enc = encodeURIComponent(playerName);
  return sb.patch("players", `name=eq.${enc}`, patch);
}

// ─── Safe PVE reward credit — read-modify-write via profile_id ────────────────
// Même logique anti-clobber que creditDuelGold (league-duel/engine/duelGold.ts) :
// les cumulables (bonus_points, match_gold, pve_trial_chests, pve_best_stage) ne
// doivent JAMAIS repartir d'un snapshot React local (potentiellement stale →
// régression en DB). On relit la ligne fraîche par profile_id, on additionne sur
// la valeur DB, puis on PATCH ciblé. Aucune récompense n'est accordée si la
// lecture OU l'écriture Supabase échoue.
//
// TODO RPC : remplacer par un RPC atomique (read-modify-write transactionnel)
// avant release publique — évite le double-crédit concurrent entre deux clients.

/** Increments to apply, computed from the run result (NOT from local state). */
export interface PveRewardCredit {
  profileId: string;
  reachedStage: number;
  bonusPointsDelta: number;
  matchGoldDelta: number;
  chestDelta: number; // 0 or 1
  conditionAfter: number;
  conditionUpdatedAt: string;
}

/** Absolute values persisted, echoed back for the caller to sync local state. */
export interface PveRewardResult {
  bonus_points: number;
  match_gold: number;
  pve_trial_chests: number;
  pve_best_stage: number;
  pve_condition: number;
  pve_condition_updated_at: string;
}

/**
 * Credit PVE rewards safely: re-reads the fresh players row by profile_id,
 * increments cumulative fields from the DB values, then patches by profile_id.
 *
 * Returns the persisted absolute values on success, or `null` if the read OR
 * the write failed. On `null` the caller MUST NOT grant rewards nor write any
 * locally-derived value.
 */
export async function creditPveRewards(credit: PveRewardCredit): Promise<PveRewardResult | null> {
  const pid = encodeURIComponent(credit.profileId);

  // 1. Relire la ligne DB fraîche (source de vérité) par profile_id.
  const rows = await sb.get(
    `players?profile_id=eq.${pid}&select=match_gold,pve_trial_chests,pve_best_stage&limit=1`
  );
  if (!rows[0]) {
    // sb.get renvoie [] sur erreur HTTP/réseau OU joueur introuvable : dans les
    // deux cas on refuse d'écrire (pas de base fiable).
    console.error(
      `[PveRewards] lecture players échouée pour profile_id="${credit.profileId}" — crédit annulé, state local inchangé`
    );
    return null;
  }

  const row = rows[0];
  const dbGold = typeof row.match_gold === "number" ? row.match_gold : 0;
  const dbChests = typeof row.pve_trial_chests === "number" ? row.pve_trial_chests : 0;
  const dbBestStage = typeof row.pve_best_stage === "number" ? row.pve_best_stage : 0;

  const patch: PveRewardResult = {
    bonus_points: 0,
    match_gold: dbGold + credit.matchGoldDelta,
    pve_trial_chests: dbChests + credit.chestDelta,
    pve_best_stage: Math.max(dbBestStage, credit.reachedStage),
    pve_condition: credit.conditionAfter,
    pve_condition_updated_at: credit.conditionUpdatedAt,
  };

  // 2. PATCH ciblé par profile_id (jamais par name).
  const ok = await sb.patch("players", `profile_id=eq.${pid}`, patch);
  if (!ok) {
    console.error(
      `[PveRewards] PATCH players échoué pour profile_id="${credit.profileId}" — crédit annulé`
    );
    return null;
  }

  console.log("[PveRewards] creditPveRewards", {
    profileId: credit.profileId,
    reachedStage: credit.reachedStage,
    dbBefore: { gold: dbGold, chests: dbChests, bestStage: dbBestStage },
    dbAfter: patch,
    timestamp: new Date().toISOString(),
  });
  return patch;
}

// ─── Progression PVE persistée stage par stage (source de vérité = players) ────
// Une seule run active par joueur. Toutes les fonctions ciblent profile_id
// (jamais players(id)). L'anti-double-reward repose sur pve_last_rewarded_stage
// (high-water-mark monotone) + un read-modify-write dans un seul PATCH.
// TODO RPC atomique avant release publique (read-modify-write non transactionnel).

/** Snapshot absolu de la progression PVE (valeurs DB confirmées). */
export interface PveProgress {
  pve_current_stage: number;
  pve_active_bonuses: string[];
  pve_run_status: string;            // 'idle' | 'active'
  pve_run_id: string | null;
  pve_last_rewarded_stage: number;
  pve_condition: number;
  pve_condition_updated_at: string | null;
  pve_best_stage: number;
  pve_trial_chests: number;
  pve_current_tier: number;
  pve_best_tier: number;
  duel_xp: number;
  match_gold: number;
  // A/B test (docs/migration_pve_ab_spells.sql — REQUIRED before deploy).
  pve_variant: string | null;        // 'control' | 'spells' | null (unassigned)
  pve_spell_loadout: string[];       // 4 spell ids of the active run (variant B)
}

const PVE_PROGRESS_COLS =
  "pve_current_stage,pve_active_bonuses,pve_run_status,pve_run_id," +
  "pve_last_rewarded_stage,pve_condition,pve_condition_updated_at," +
  "pve_best_stage,pve_trial_chests,pve_current_tier,pve_best_tier,duel_xp,match_gold," +
  "pve_variant,pve_spell_loadout";

/** UUID v4 côté app (crypto.randomUUID si dispo, sinon getRandomValues). */
function genRunId(): string {
  const c: any = typeof crypto !== "undefined" ? crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0"));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}

const asInt = (v: unknown, d: number): number => (typeof v === "number" ? v : d);
const asBonuses = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

function rowToProgress(row: any): PveProgress {
  return {
    pve_current_stage: asInt(row.pve_current_stage, 1),
    pve_active_bonuses: asBonuses(row.pve_active_bonuses),
    pve_run_status: typeof row.pve_run_status === "string" ? row.pve_run_status : "idle",
    pve_run_id: typeof row.pve_run_id === "string" ? row.pve_run_id : null,
    pve_last_rewarded_stage: asInt(row.pve_last_rewarded_stage, 0),
    pve_condition: asInt(row.pve_condition, 100),
    pve_condition_updated_at: typeof row.pve_condition_updated_at === "string" ? row.pve_condition_updated_at : null,
    pve_best_stage: asInt(row.pve_best_stage, 0),
    pve_trial_chests: asInt(row.pve_trial_chests, 0),
    pve_current_tier: asInt(row.pve_current_tier, 1),
    pve_best_tier: asInt(row.pve_best_tier, 1),
    duel_xp: asInt(row.duel_xp, 0),
    match_gold: asInt(row.match_gold, 0),
    pve_variant: typeof row.pve_variant === "string" ? row.pve_variant : null,
    pve_spell_loadout: asBonuses(row.pve_spell_loadout),
  };
}

/** Lit la progression PVE fraîche depuis Supabase. Null si lecture échouée. */
export async function getPveProgress(profileId: string): Promise<PveProgress | null> {
  const pid = encodeURIComponent(profileId);
  const rows = await sb.get(`players?profile_id=eq.${pid}&select=${PVE_PROGRESS_COLS}&limit=1`);
  if (!rows[0]) {
    console.error(`[Pve] getPveProgress échouée pour profile_id="${profileId}"`);
    return null;
  }
  return rowToProgress(rows[0]);
}

/**
 * Démarre une nouvelle run OU reprend la run active.
 * - status 'active' → retourne la progression telle quelle (reprise).
 * - sinon → crée une run fraîche (run_id côté app), stage 1, bonuses [], status
 *   'active'. La Condition n'est PAS reset (elle regen normalement).
 * Retourne le snapshot après opération, ou null si échec.
 */
export async function startOrResumePveRun(profileId: string): Promise<PveProgress | null> {
  const current = await getPveProgress(profileId);
  if (!current) return null;
  if (current.pve_run_status === "active") return current; // reprise

  const runId = genRunId();
  const pid = encodeURIComponent(profileId);
  const ok = await sb.patch("players", `profile_id=eq.${pid}`, {
    pve_run_status: "active",
    pve_run_id: runId,
    pve_current_stage: 1,
    pve_active_bonuses: [],
    pve_spell_loadout: [], // variant B picks a fresh loadout at run start
    pve_last_rewarded_stage: 0,
  });
  if (!ok) {
    console.error(`[Pve] startOrResumePveRun PATCH échoué pour profile_id="${profileId}"`);
    return null;
  }
  return {
    ...current,
    pve_run_status: "active",
    pve_run_id: runId,
    pve_current_stage: 1,
    pve_active_bonuses: [],
    pve_spell_loadout: [],
    pve_last_rewarded_stage: 0,
  };
}

/** Increments + contexte pour créditer un stage gagné. */
export interface PveStageWin {
  profileId: string;
  runId: string;
  stage: number;                 // stage gagné (1..10)
  xpDelta: number;
  goldDelta: number;
  chestDelta: number;            // 0 | 1 (crédité si stage 10)
  conditionAfter: number;
  activeBonuses: string[];
}

/** Résultat de savePveStageWin. `already` = garde a bloqué (pas de double crédit). */
export type PveStageWinResult =
  | { status: "credited"; progress: PveProgress }
  | { status: "already"; progress: PveProgress }
  | { status: "error" };

/**
 * Crédite un stage gagné et avance la run, en UN SEUL read-modify-write guardé.
 * Gardes (anti-double-reward) : run_id client == DB, current_stage == stage,
 * last_rewarded_stage < stage. Si la garde bloque → 'already' (aucun crédit).
 * Rewards crédités depuis les valeurs DB fraîches, jamais depuis le local.
 * N'avance pas le stage si stage 10 (le reset terminal se fait via resetPveRun).
 */
export async function savePveStageWin(win: PveStageWin): Promise<PveStageWinResult> {
  const fresh = await getPveProgress(win.profileId);
  if (!fresh) return { status: "error" };

  // Gardes d'idempotence.
  if (fresh.pve_last_rewarded_stage >= win.stage) return { status: "already", progress: fresh };
  if (fresh.pve_run_id !== win.runId || fresh.pve_current_stage !== win.stage) {
    console.warn("[Pve] savePveStageWin garde échouée", {
      expectedRun: win.runId, dbRun: fresh.pve_run_id,
      expectedStage: win.stage, dbStage: fresh.pve_current_stage,
    });
    return { status: "already", progress: fresh };
  }

  const isFinal = win.stage >= 10;
  // XP goes to duel_xp — same pool as League Duel: pure level XP, no side
  // effect on points/gold (unlike bonus_points, which also feeds the wallet).
  const patch = {
    duel_xp: fresh.duel_xp + win.xpDelta,
    match_gold: fresh.match_gold + win.goldDelta,
    pve_trial_chests: fresh.pve_trial_chests + win.chestDelta,
    pve_best_stage: Math.max(fresh.pve_best_stage, win.stage),
    pve_last_rewarded_stage: win.stage,
    pve_current_stage: isFinal ? win.stage : win.stage + 1,
    pve_active_bonuses: win.activeBonuses,
    // pve_condition = % décimal (source de vérité régen). Jamais arrondi avant write.
    pve_condition: win.conditionAfter,
    pve_condition_updated_at: new Date().toISOString(),
  };

  const pid = encodeURIComponent(win.profileId);
  const ok = await sb.patch("players", `profile_id=eq.${pid}`, patch);
  if (!ok) {
    console.error(`[Pve] savePveStageWin PATCH échoué pour profile_id="${win.profileId}", stage ${win.stage}`);
    return { status: "error" };
  }

  console.log("[Pve] savePveStageWin", { profileId: win.profileId, stage: win.stage, patch });
  return { status: "credited", progress: { ...fresh, ...patch } };
}

/** Sauvegarde immédiate des boosts actifs (au moment du choix). Boolean succès. */
export async function savePveBonusChoice(profileId: string, bonuses: string[]): Promise<boolean> {
  const pid = encodeURIComponent(profileId);
  const ok = await sb.patch("players", `profile_id=eq.${pid}`, { pve_active_bonuses: bonuses });
  if (!ok) console.error(`[Pve] savePveBonusChoice PATCH échoué pour profile_id="${profileId}"`);
  return ok;
}

/** Variante B — persiste le loadout de 4 sorts choisi au start de run. */
export async function savePveSpellLoadout(profileId: string, spells: string[]): Promise<boolean> {
  const pid = encodeURIComponent(profileId);
  const ok = await sb.patch("players", `profile_id=eq.${pid}`, { pve_spell_loadout: spells });
  if (!ok) console.error(`[Pve] savePveSpellLoadout PATCH échoué pour profile_id="${profileId}"`);
  return ok;
}

/**
 * Persiste l'assignation A/B (une seule fois, au premier passage PVE quand le
 * hash rollout est actif). Fire-and-forget côté appelant : un échec n'empêche
 * pas de jouer (la variante effective est recalculée par resolvePveVariant).
 */
export async function savePveVariant(profileId: string, variant: string): Promise<boolean> {
  const pid = encodeURIComponent(profileId);
  const ok = await sb.patch("players", `profile_id=eq.${pid}`, { pve_variant: variant });
  if (!ok) console.error(`[Pve] savePveVariant PATCH échoué pour profile_id="${profileId}"`);
  return ok;
}

/**
 * Termine le palier courant (stage 10 gagné) : avance pve_current_tier de +1,
 * met pve_best_tier à jour, et reset la run (idle, stage 1). NE recrédite RIEN
 * (savePveStageWin(10) a déjà crédité XP/gold/coffre). Garde la Condition.
 * Retourne les nouveaux tiers, ou null si lecture/écriture échoue.
 */
export async function completePveTier(
  profileId: string,
  conditionAfter: number
): Promise<{ pve_current_tier: number; pve_best_tier: number } | null> {
  const pid = encodeURIComponent(profileId);
  const rows = await sb.get(`players?profile_id=eq.${pid}&select=pve_current_tier,pve_best_tier&limit=1`);
  if (!rows[0]) {
    console.error(`[Pve] completePveTier lecture échouée pour profile_id="${profileId}"`);
    return null;
  }
  const curTier = asInt(rows[0].pve_current_tier, 1);
  const bestTier = asInt(rows[0].pve_best_tier, 1);
  const nextTier = curTier + 1;
  const nextBest = Math.max(bestTier, nextTier);

  const ok = await sb.patch("players", `profile_id=eq.${pid}`, {
    pve_current_tier: nextTier,
    pve_best_tier: nextBest,
    pve_current_stage: 1,
    pve_last_rewarded_stage: 0,
    pve_active_bonuses: [],
    pve_spell_loadout: [],
    pve_run_status: "idle",
    pve_run_id: null,
    pve_condition: conditionAfter, // % décimal — jamais arrondi avant write
    pve_condition_updated_at: new Date().toISOString(),
  });
  if (!ok) {
    console.error(`[Pve] completePveTier PATCH échoué pour profile_id="${profileId}"`);
    return null;
  }
  return { pve_current_tier: nextTier, pve_best_tier: nextBest };
}

/**
 * Reset la run au stage 1 après défaite ou stage 10 terminé. Garde la Condition
 * (conditionAfter, jamais 0). Les rewards déjà crédités par stage restent.
 * Boolean succès.
 */
export async function resetPveRun(profileId: string, conditionAfter: number): Promise<boolean> {
  const pid = encodeURIComponent(profileId);
  const ok = await sb.patch("players", `profile_id=eq.${pid}`, {
    pve_current_stage: 1,
    pve_last_rewarded_stage: 0,
    pve_active_bonuses: [],
    pve_spell_loadout: [],
    pve_run_status: "idle",
    pve_run_id: null,
    pve_condition: conditionAfter, // % décimal — jamais arrondi avant write
    pve_condition_updated_at: new Date().toISOString(),
  });
  if (!ok) console.error(`[Pve] resetPveRun PATCH échoué pour profile_id="${profileId}"`);
  return ok;
}
